import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { FieldRenderer } from "../../components/admin/Fields";

const SECTIONS = [
  {
    key: "hero",
    title: "Inicio (Hero)",
    fields: [
      { key: "title", label: "Título principal", type: "text" },
      { key: "subtitle", label: "Subtítulo", type: "textarea" },
      { key: "ecosystem", label: "Ecosistema (separado por ·)", type: "text" },
    ],
  },
  {
    key: "about",
    title: "Nosotros",
    fields: [
      { key: "title", label: "Título", type: "text" },
      { key: "paragraphs", label: "Párrafos", type: "list" },
      { key: "points", label: "Puntos clave", type: "list" },
      { key: "image", label: "Imagen", type: "image" },
    ],
  },
  {
    key: "contact",
    title: "Contacto",
    fields: [
      { key: "phone", label: "Teléfono (visible)", type: "text" },
      { key: "whatsapp", label: "WhatsApp (solo números, ej. 50496390722)", type: "text" },
      { key: "email", label: "Correo", type: "text" },
      { key: "website", label: "Sitio web", type: "text" },
      { key: "location", label: "Ubicación", type: "text" },
    ],
  },
  {
    key: "socials",
    title: "Redes sociales",
    fields: [
      { key: "facebook", label: "Facebook URL", type: "text" },
      { key: "instagram", label: "Instagram URL", type: "text" },
      { key: "tiktok", label: "TikTok URL", type: "text" },
    ],
  },
  {
    key: "seo",
    title: "SEO",
    fields: [
      { key: "title", label: "Título SEO", type: "text" },
      { key: "description", label: "Meta descripción", type: "textarea" },
      { key: "keywords", label: "Keywords", type: "text" },
    ],
  },
];

export default function Settings() {
  const qc = useQueryClient();
  const [data, setData] = useState(null);

  const { data: fetched } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: async () => (await api.get("/admin/settings")).data,
  });

  useEffect(() => {
    if (fetched) setData(fetched);
  }, [fetched]);

  const saveMut = useMutation({
    mutationFn: (payload) => api.put("/admin/settings", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      qc.invalidateQueries({ queryKey: ["public-site"] });
      toast.success("Configuración guardada");
    },
    onError: () => toast.error("Error al guardar (¿rol administrador?)"),
  });

  const setField = (section, key, value) =>
    setData((p) => ({ ...p, [section]: { ...(p[section] || {}), [key]: value } }));

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Configuración</h1>
          <p className="mt-1 text-sm text-white/50">Textos, contacto, redes y SEO del sitio</p>
        </div>
        <button onClick={() => saveMut.mutate(data)} disabled={saveMut.isPending} data-testid="settings-save-btn" className="inline-flex items-center gap-2 rounded-full bg-glg-blue px-5 py-2.5 text-sm font-semibold text-white hover:scale-[1.03] disabled:opacity-60">
          {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
        </button>
      </div>

      <div className="mt-8 space-y-6">
        {SECTIONS.map((sec) => (
          <div key={sec.key} className="rounded-2xl border border-white/10 bg-glg-surface/60 p-6">
            <h2 className="mb-5 font-heading text-lg font-semibold text-white">{sec.title}</h2>
            <div className="space-y-5">
              {sec.fields.map((f) => (
                <div key={f.key}>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-white/50">{f.label}</label>
                  <FieldRenderer field={f} value={(data[sec.key] || {})[f.key]} onChange={(v) => setField(sec.key, f.key, v)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
